import { useState } from 'react';
import { LoaderIcon } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SingleContract from './SingleContract';
import useUserContractsPagination, {
  PContract,
} from './useUserContractsPagination';
import { TFunction } from 'i18next';
import { StatusFilter, useParameters } from '@web/utils';
import filterIcon from '@web/utils/src/assets/icons/functions/filter-icon.svg';
import {
  StyledPagination,
  StyledTable,
} from '@/commons/ui/theme/materialStyles';
import { ContractStatus } from '@/types';
import { CircularProgress, TableCell } from '@mui/material';

type TableKey = keyof PContract;

const TABLE_HEAD = (
  t: TFunction,
): {
  id: TableKey;
  label: string;
  alignRight: boolean;
}[] => [
  {
    id: 'retailer',
    label: t('contracts.table_columns.retailer'),
    alignRight: false,
  },
  {
    id: 'status',
    label: t('contracts.table_columns.status'),
    alignRight: false,
  },
  {
    id: 'expectedDeliveryAt',
    label: t('contracts.table_columns.date'),
    alignRight: false,
  },
  {
    id: 'erpID',
    label: t('contracts.table_columns.contract_number'),
    alignRight: false,
  },
  {
    id: 'lineItems',
    label: t('contracts.table_columns.delivered'),
    alignRight: false,
  },
];

const DEFAULT_STATUSES = [
  ContractStatus.Confirmed,
  ContractStatus.PartiallyFulfilled,
];

const STATUS_OPTIONS = [
  ContractStatus.Confirmed,
  ContractStatus.PartiallyFulfilled,
  ContractStatus.Fullfilled,
  ContractStatus.Cancelled,
];

const ContractsTable = () => {
  const [selectedStatuses, setSelectedStatuses] =
    useState<ContractStatus[]>(DEFAULT_STATUSES);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { retailerId } = useParameters<{ retailerId: string }>();
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [filterOpen, setFilterOpen] = useState(false);

  const { result, loadMore, fetching, loadPrevious, total } =
    useUserContractsPagination(retailerId ?? '', perPage, selectedStatuses);

  // Derive the results for the current page
  const paginatedResults = result.slice(page * perPage, (page + 1) * perPage);

  const handleRowClick = (contract: PContract) => {
    navigate(`/documents/${retailerId}/contracts/${contract.id}`);
  };

  const handlePageChange = (e, newPage) => {
    if (newPage > page) {
      loadMore();
      setPage((val) => val + 1);
    } else {
      loadPrevious();
      setPage((val) => val - 1);
    }
  };

  const handleRowsPerPageChange = (e) => {
    setPerPage(e.target.value);
  };

  const toggleFilter = () => {
    setFilterOpen(!filterOpen);
  };

  const handleFilterChange = (statuses: ContractStatus[]) => {
    setSelectedStatuses(statuses);
    setFilterOpen(false);
  };

  return (
    <div className="py-2-0">
      <div className="mb-1-0 flex justify-between items-start">
        <div
          className="flex items-center cursor-pointer mr-2-0"
          onClick={toggleFilter}
        >
          <img src={filterIcon} className="w-2-0 h-2-0" />
          <span className="text-1-6 ml-0-5">{t('contracts.filter')}</span>
        </div>
        <StatusFilter
          onFilterChange={handleFilterChange}
          defaultStatuses={DEFAULT_STATUSES}
          statusOptions={STATUS_OPTIONS}
          isOpen={filterOpen}
          t={t}
        />
      </div>
      <StyledTable stickyHeader>
        <thead>
          <tr>
            {TABLE_HEAD(t).map((headCell) => (
              <TableCell
                key={headCell.id}
                align={headCell.alignRight ? 'right' : 'left'}
              >
                <span className="text-body1 font-rg opacity-80 capitalize">
                  {headCell.label}
                </span>
              </TableCell>
            ))}
          </tr>
        </thead>
        <tbody>
          {paginatedResults.map((contract) => (
            <SingleContract
              contract={contract}
              handleClick={handleRowClick}
              key={contract.id}
            />
          ))}
          <tr style={{ width: '100%' }}>
            <StyledPagination
              count={total}
              page={page}
              rowsPerPage={perPage}
              rowsPerPageOptions={[20, 50, 100]}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              labelRowsPerPage={t('global.rows_per_age')}
            />
          </tr>
        </tbody>
      </StyledTable>
      {fetching && <LoaderIcon className="mx-auto mt-10-0" />}
      {!fetching && result.length === 0 && (
        <p className="text-body1 py-4-0 font-bd text-center">
          {t('contracts.contracts_not_found')}
        </p>
      )}
    </div>
  );
};

export default ContractsTable;
